import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import {
  type Options as ReactHotkeyOptions,
  useHotkeys as useReactHotkey,
} from "react-hotkeys-hook";

// TODO (ado-277922): remove this `hasBdsHotkey` interop with the BDS HotkeyManager once all BDS
// hotkey usages are migrated to the BTNet implementation (see usage in commandWrapper below).
import { hasBdsHotkey } from "@buildertrend/components";

import { FocusContext } from "../Focus/FocusContext";
import { type AvailableTags } from "./hotkey.types";

interface IUseHotkeyArgs {
  command: string;
  onCommand?: (() => void) | undefined;
  triggerBehavior?: "press" | "up" | "down" | undefined;
  disabled?: boolean | undefined;
  preventDefaultWhenDisabled?: boolean | undefined;
  supportedTags?: AvailableTags[] | undefined;
}

export function useHotkey({
  command,
  onCommand,
  supportedTags,
  triggerBehavior = "press",
  preventDefaultWhenDisabled = true,
  disabled = false,
}: IUseHotkeyArgs) {
  const commandWrapper = useCallback(
    (e: KeyboardEvent) => {
      // TODO (ado-277922): drop the `!hasBdsHotkey(e)` guard once BDS no longer owns any
      // hotkeys — it only exists so we don't preventDefault on a key a BDS component handles.
      if ((!disabled || preventDefaultWhenDisabled) && !hasBdsHotkey(e)) {
        e?.preventDefault();
        e?.stopPropagation();
      }
      if (!disabled && !e.repeat) {
        onCommand?.();
      }
    },
    [disabled, preventDefaultWhenDisabled, onCommand],
  );

  const commandWrapperStaticRef = useRef(commandWrapper);
  useEffect(() => {
    commandWrapperStaticRef.current = commandWrapper;
  }, [commandWrapper]);

  const focusContext = useContext(FocusContext);
  const commandOptions = useMemo<ReactHotkeyOptions>(() => {
    const keydown =
      triggerBehavior === "down"
        ? true
        : triggerBehavior !== "up"
          ? undefined
          : false;
    const keyup =
      triggerBehavior === "up"
        ? true
        : triggerBehavior !== "down"
          ? undefined
          : false;
    const options: ReactHotkeyOptions = {
      filter: () => {
        return focusContext?.isCurrentFocus ?? false;
      },
      filterPreventDefault: false,
      enableOnTags: supportedTags ?? ["INPUT", "SELECT", "TEXTAREA"],
      enableOnContentEditable: true,
      // Only set keydown/keyup when defined — exactOptionalPropertyTypes forbids
      // assigning `undefined` to these optional react-hotkeys-hook options.
      ...(keydown !== undefined && { keydown }),
      ...(keyup !== undefined && { keyup }),
    };
    return options;
  }, [supportedTags, focusContext, triggerBehavior]);

  useReactHotkey(
    command,
    (e) => commandWrapperStaticRef.current(e),
    commandOptions,
  );
}
